import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Typography, Divider } from "@mui/material";
import { uploadNewQTOApi, exportQTOApi, QTOhistoryApi, exportQTOTemplateApi } from "../../../api/qto";
import { getFileSize } from "../../../utils/file.js";
import { NotificationContext } from "../../../component/popup/NotificationProvider";
import dayjs from "dayjs";
import Popup from "../../../component/popup/Popup.js";
import Table from "../../../component/table/MainTable.js";
import FileList from "../../../component/FileList.js";
import UploadNewFileButton from "../../../component/button/UploadNewFileButton.js";
import DownloadTemplateButton from "../../../component/button/DownloadTemplateButton.js";
import ExportDataButton from "../../../component/button/ExportDataButton.js";
import FullScreenLoading from "../../../component/FullScreenLoading.js";

const ManageQTOPopup = ({ open, onClose, onUploadSuccess, user }) => {
  const { projectId } = useParams();
  const { showSuccess, showError } = useContext(NotificationContext);
  const readonly = !user?.editRight;
  const [fileHistory, setFileHistory] = useState(null);
  const [latestFile, setLatestFile] = useState([]);
  const [isUploadButtonDisabled, setIsUploadButtonDisabled] = useState(false);
  const [isLoadingOperation, setIsLoadingOperation] = useState(false);

  const modelHistoryHead = [
    { id: "filename", label: "Archive File Name", align: "left" },
    { id: "fileSize", label: "Size", align: "left" },
    { id: "uploadDate", label: "Upload Date", align: "left", type: "dateWithTime" },
    { id: "uploadBy", label: "Upload By", align: "left" },
    { id: "icon", label: "", align: "center" },
  ];

  const getTableData = useCallback(async () => {
    try {
      const result = await QTOhistoryApi(projectId);
      const data = result?.data;

      if (!data || !Array.isArray(data)) {
        showError("Invalid or empty table data received.");
        return;
      }

      const formattedData = data.map((item) => ({
        ...item,
        uploadDate: dayjs(item.uploadDate).format("YYYY-MM-DD HH:mm"),
        fileSize: item.fileSize ? getFileSize(item.fileSize) : 0,
      }));

      const activeItem = data.find((item) => item.status === "active");
      if (activeItem) {
        setLatestFile([activeItem]);
      }
      setFileHistory(formattedData);
    } catch {
      showError("Received table data occurred error.");
    }
  }, []);

  const uploadNewQTO = async (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      setIsLoadingOperation(true);
      const formData = new FormData();
      formData.append("file", files[0]);
      try {
        const result = await uploadNewQTOApi(projectId, formData);
        if (result) {
          showSuccess(
            "The QTO template has been successfully uploaded to the project."
          );
          getTableData();
          onUploadSuccess();
        } else {
          showError("Upload failed.");
        }
      } catch (error) {
        if (
          error.status === 422 &&
          error.response &&
          error.response.data &&
          error.response.data.detail
        ) {
          showError(error.response.data.detail);
        } else if (
          error.status === 500 &&
          error.response && 
          error.response.data &&
          error.response.data.detail
        ) {
          if (typeof error.response.data.detail === 'string') {
            showError(error.response.data.detail);
          } else {
            let errorMessage = "";
            let hasError = false;

            const duplicates = (error.response.data.detail?.duplicateIds || [])
              .filter(item => {
                if (typeof item === 'string') {
                  return item.trim() !== '';
                }
                return item !== null && item !== undefined;
              });
            if (Array.isArray(duplicates) && duplicates.length > 0) {
              const duplicatesString = duplicates.join(",\n");
              errorMessage += `<strong>Following QTO ID(s) are duplicated in the list:</strong><br />${duplicatesString}.<br /><br /><strong>Please correct them before upload.</strong><br />`;
              hasError = true;
            }

            const invalidNumeric = (error.response.data.detail?.invalidNumericInput || [])
              .filter(item => {
                if (typeof item === 'string') {
                  return item.trim() !== '';
                }
                return item !== null && item !== undefined;
              });
            if (
              Array.isArray(invalidNumeric) &&
              invalidNumeric.length > 0
            ) {
              const invalidNumericString = invalidNumeric.join(",\n");
              errorMessage += `<strong>Following QTO ID(s) found the invalid numeric input:</strong><br />${invalidNumericString}.<br /><br /><strong>Please correct them before upload.</strong><br />`;
              hasError = true;
            }

            const missingQuantityUnit = (error.response.data.detail?.missingQuantityUnitOfMeasure || [])
              .filter(item => {
                if (typeof item === 'string') {
                  return item.trim() !== '';
                }
                return item !== null && item !== undefined;
              });
            if (
              Array.isArray(missingQuantityUnit) &&
              missingQuantityUnit.length > 0
            ) {
              const quantityUnitString = missingQuantityUnit.join(",\n");
              errorMessage += `<strong>Following QTO(s) are missing a ‘Quantity Unit of measure’:</strong><br />${quantityUnitString}.<br /><br /><strong>Please correct them before upload.</strong><br />`;
              hasError = true;
            }

            const missingBaseQTO = (error.response.data.detail?.missingBaseQuantity || [])
              .filter(item => {
                if (typeof item === 'string') {
                  return item.trim() !== '';
                }
                return item !== null && item !== undefined;
              });
            if (
              Array.isArray(missingBaseQTO) &&
              missingBaseQTO.length > 0
            ) {
              const missingBaseString = missingBaseQTO.join(",\n");
              errorMessage += `<strong>Following QTO(s) are missing a Base quantity when ‘Quantity Unit of measure’ is not set to 'no's', 'volume', or 'length':</strong><br />${missingBaseString}.<br /><br /><strong>Please correct them before upload.</strong><br />`;
              hasError = true;
            }

            if (hasError) {
              showError(errorMessage);
            } else {
              showError("Upload failed.");
            }
          }
        } else {
          showError("Upload failed.");
        }
      } finally {
        setIsLoadingOperation(false);
      }
    }
  };

  const downloadFile = (item) => {
    setIsLoadingOperation(true);
    const link = document.createElement("a");
    link.href = item?.fileURL;
    link.download = item?.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      showSuccess("The file has been successfully downloaded.");
      setIsLoadingOperation(false);
    }, 3000);
  };

  const downloadQTOTemplate = async () => {
    setIsLoadingOperation(true);
    try {
      const result = await exportQTOTemplateApi(projectId);
      if (result) {
        const data = result?.data;
        const link = document.createElement("a");
        link.href = data;
        link.download = data?.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSuccess("The file has been successfully downloaded.");
      } else {
        showError("Download failed.");
      }
    } catch {
      showError("Download failed.");
    } finally {
      setIsLoadingOperation(false);
    }
  };

  const exportCurrentData = async () => {
    setIsLoadingOperation(true);
    try {
      const result = await exportQTOApi(projectId);
      if (result) {
        const data = result?.data;
        const link = document.createElement("a");
        link.href = data;
        link.download = data?.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSuccess("The file has been successfully downloaded.");
      } else {
        showError("Export failed.");
      }
    } catch {
      showError("Export failed.");
    } finally {
      setIsLoadingOperation(false);
    }
  };

  useEffect(() => {
    getTableData();
  }, [getTableData]);

  useEffect(() => {
    if (fileHistory && fileHistory.length > 0) {
      setIsUploadButtonDisabled(true);
    } else {
      setIsUploadButtonDisabled(false);
    }
  }, [fileHistory]);

  return (
    <>
      <Popup
        open={open}
        onClose={onClose}
        modalStyle={{
          "& .MuiBox-root.css-ft9d2q": {
            borderRadius: "10px 10px 0 0",
          },
        }}
        popupStyle={{
          minWidth: 1000,
          maxWidth: 1000,
        }}
        title="Manage QTO"
      >
        <Box maxHeight={800} overflow="auto">
          <Box
            sx={{
              mx: 4,
              my: 2,
              p: 2,
              background: "#F5F5F5",
              borderRadius: "10px",
            }}
          >
            <Typography variant="h6" component="span">
              Quantity Take-off
            </Typography>
            <Typography component="span">
              &nbsp;&nbsp;* QTO files shall be in Excel format
            </Typography>
            <Divider variant="fullWidth" />
            <Box sx={{ display: "flex", mb: 3, gap: 3 }}>
              <Typography component="span" sx={{ pt: 1.5 }}>
                current:
              </Typography>
              <FileList
                files={latestFile}
                onUpload={(event, type) => uploadNewQTO(event, type)}
                showRemoveButton={false}
                accept=".xlsx,.xls,.xlsm,.xml"
                onDownload={downloadFile}
                disabled={readonly}
              />
            </Box>
            <Table
              columns={modelHistoryHead}
              rows={fileHistory}
              iconColumnName="icon"
              headerStyles={{
                default: { backgroundColor: "#D0D5DE" },
              }}
              iconImage={"assets/icons/clonedownload.svg"}
              columnWidths={{
                icon: "100px",
                fileSize: "130px",
                uploadDate: "160px",
                uploadBy: "130px",
              }}
              sx={{ maxHeight: 200 }}
              onHistoryUploadSuccess={() =>
                showSuccess("The file has been successfully downloaded.")
              }
            />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={2}
              mt={2}
            >
              <UploadNewFileButton
                accept=".xlsx,.xls,.xlsm,.xml"
                disabled={isUploadButtonDisabled}
                onChange={(event) => {
                  uploadNewQTO(event);
                }}
              ></UploadNewFileButton>
              <DownloadTemplateButton
                onClick={() => downloadQTOTemplate()}
                disabled={readonly}
              ></DownloadTemplateButton>
              <ExportDataButton
                onClick={() => exportCurrentData()}
              ></ExportDataButton>
            </Box>
          </Box>
        </Box>
        <Box textAlign="center">
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            sx={{ borderRadius: "10px", lineHeight: "normal", mb: 2 }}
          >
            Back
          </Button>
        </Box>
      </Popup>
      <FullScreenLoading isLoading={isLoadingOperation} />
    </>
  );
};

export default ManageQTOPopup;
